/**
 * Cycle Info pane – title, duration (shared at top of wizard)
 */
import React from "react";
import { translateUi } from '../i18n/index';
import { useCycleWizard } from "./CycleWizardStore";
import { cb, cbCn } from "./cycleBuilderTheme";

export function CycleInfoPane({
  isMobile,
  isDarkMode = false,
  embedded = false,
}: {
  isMobile?: boolean;
  isDarkMode?: boolean;
  /** Skip outer panel chrome when nested in CollapsiblePane. */
  embedded?: boolean;
}) {
  const { state, dispatch, durationInMonths } = useCycleWizard();
  const locked = !!state.cycleInfoLocked;
  const inputBase = "mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-1";

  const body = (
    <>
      {!embedded && <h3 className={cb.heading(isDarkMode, !isMobile)}>{translateUi("Cycle Info")}</h3>}
      {locked && (
        <p className={cbCn(embedded ? "text-sm" : "mt-2 text-sm", cb.body(isDarkMode))}>
          {translateUi("Title and duration come from the selected template and cannot be changed here.")}
        </p>
      )}
      <div className={cbCn(embedded && !locked ? "" : "mt-4", "grid gap-4", isMobile ? "grid-cols-1" : "sm:grid-cols-2")}>
        <div>
          <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}> {translateUi("Title")} </label>
          <input
            type="text"
            value={state.title}
            onChange={(e) => dispatch({ type: "SET_TITLE", title: e.target.value })}
            placeholder="e.g. Train Driver Competency Cycle"
            disabled={locked}
            className={cbCn(inputBase, cb.input(isDarkMode, locked))}
          />
        </div>
        <div className={cbCn("flex gap-2", isMobile ? "flex-col" : "items-end")}>
          <div className={isMobile ? "w-full" : "w-[5.25rem] shrink-0"}>
            <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}> {translateUi("Duration")} </label>
            <input
              type="number"
              min={1}
              max={20}
              value={state.durationValue}
              onChange={(e) =>
                dispatch({ type: "SET_DURATION", value: Number(e.target.value) || 1, unit: state.durationUnit })
              }
              disabled={locked}
              className={cbCn(inputBase, "min-w-0 tabular-nums sm:px-2.5", cb.input(isDarkMode, locked))}
            />
          </div>
          <div className={isMobile ? "w-full" : "min-w-[9.5rem] flex-1"}>
            <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}> {translateUi("Unit")} </label>
            <select
              value={state.durationUnit}
              onChange={(e) =>
                dispatch({
                  type: "SET_DURATION",
                  value: state.durationValue,
                  unit: e.target.value as "months" | "years",
                })
              }
              disabled={locked}
              className={cbCn(inputBase, cb.input(isDarkMode, locked))}
            >
              <option value="months">{translateUi("Months")}</option>
              <option value="years"> {translateUi("Years")} </option>
            </select>
          </div>
        </div>
      </div>
      <p className={cbCn("mt-2 text-sm", cb.bodySm(isDarkMode))}>Total duration: {durationInMonths} months</p>
    </>
  );

  if (embedded) return <div>{body}</div>;
  return <div className={cb.panel(isDarkMode, isMobile ? "sm" : "md")}>{body}</div>;
}
